import React from 'react';
import { useCallback, useEffect, useState } from 'react';
import {
  CodeDecodeValueListRequest,
  useFetchCodeDecodeValuesQuery,
} from '../codedecodevalue-api';
import AuditLogsScreen from '../../../components/common/auditLogs';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '@ant-design/pro-layout';
import {
  Alert,
  Badge,
  Breadcrumb,
  Button,
  Card,
  Dropdown,
  Input,
  Menu,
  message,
  Modal,
  Space,
  Table,
} from 'antd';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  HomeOutlined,
  MinusCircleFilled,
  MoreOutlined,
  InfoCircleOutlined,
  WarningFilled,
} from '@ant-design/icons';
import {
  FilterValue,
  SorterResult,
  TableCurrentDataSource,
  TablePaginationConfig,
} from 'antd/lib/table/interface';
import { CodeDecodeValueViewModel, PaginatedResult } from '../../api/models';
import { DefaultPage, DefaultPageSize, DefaultPollingInterval } from '../../../core/defaults';
import Title from 'antd/lib/typography/Title';
import { getLink } from '../../../App-router';
import { TableState } from '../../../components/table/model';
import getSortBy from '../../../components/table/sorter';
import { useMasterDataDelete } from '../../masterdata/useMasterDataDelete';

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

export default function CodeDecodeValuesScreen(props: any) {
  const location = useLocation();
  const query = useQuery();
  const params: any = useParams<any>();
  const navigate = useNavigate();
  const [showAuditLogs, setShowAuditLogs] = useState<boolean>(false);
  const [entityId, setEntityId] = useState<number>(0);

  // Helper function to get sortOrder for a column based on current URL state
  const getSortOrderForColumn = (columnKey: string): 'ascend' | 'descend' | null => {
    const sortBy = query.get('sortBy') || '';
    if (!sortBy) return null;
    
    const columnMapping: { [key: string]: string } = {
      'Id': 'Id',
      'code': 'Code',
    };
    
    const backendColumnName = columnMapping[columnKey] || columnKey;
    const sortByParts = sortBy.trim().split(/\s+/);
    const sortColumnName = sortByParts[0];
    const sortDirection = sortByParts[1]?.toLowerCase() || '';
    
    if (sortColumnName.toLowerCase() === backendColumnName.toLowerCase()) {
      return sortDirection === 'desc' ? 'descend' : 'ascend';
    }
    return null;
  };

  const codedecodevalueTable: TableState = {
    columns: [
      {
        title: 'CodeDecodeValue',
        dataIndex: '',
        key: 'Id',
        render: (codedecodevalue: CodeDecodeValueViewModel) => (
          <>
              <Space>
                <span>{codedecodevalue.id}</span>
              </Space>
          </>
        ),
        sorter: true,
        sortOrder: getSortOrderForColumn('Id'),
        fixed: 'left',
      },
      {
        title: 'Code',
        dataIndex: '',
        key: 'code',
        render: (codedecodevalue: CodeDecodeValueViewModel) => (
          <>
              <span>{codedecodevalue.code}</span>
          </>
        ),
        sorter: true,
        sortOrder: getSortOrderForColumn('code'),
      },
      {
        title: 'Decode',
        dataIndex: '',
        key: 'decode',
        render: (codedecodevalue: CodeDecodeValueViewModel) => (
          <>
              <span>{codedecodevalue.decode}</span>
          </>
        ),
      },
      {
        title: 'Decode1',
        dataIndex: '',
        key: 'decode1',
        render: (codedecodevalue: CodeDecodeValueViewModel) => (
          <>
              <span>{codedecodevalue.decode1}</span>
          </>
        ),
      },
      {
        title: 'CodeDecodeType',
        key: 'codeDecodeType',
        dataIndex: '',
        render: (codedecodevalue: CodeDecodeValueViewModel) => (
          <>
              <span>{codedecodevalue.codeDecodeType}</span>
          </>
        ),
      },      
      {
        title: 'Actions',
        key: 'action',
        dataIndex: '',
        fixed:'right',
        render: (codedecodevalue: CodeDecodeValueViewModel) => (
          <Dropdown
            trigger={['click']}
            placement="bottomRight"
            overlay={
              <Menu style={{ padding: '8px 8px' }}>
                <Menu.Item icon={<EditOutlined />} key="1">
                  <Link
                    to={getLink('CodeDecodeValueEdit', {
                      tenantId: params.tenantId,
                      Id: codedecodevalue.id,
                      codedecodetypeid: params.codedecodetypeid
                    })}
                  >
                    Edit
                  </Link>
                </Menu.Item>
                <Menu.Item icon={<InfoCircleOutlined />} key="3">
                <Link to="#" onClick={()=>setShowAuditLogs(true)}>
                    Audit Logs
                  </Link>
                </Menu.Item>
                <Menu.Item
                  icon={<DeleteOutlined />}
                  key="delete"
                  disabled={!!(deletingId && deletingId === codedecodevalue.id)}
                  onClick={() => deleteEntity(codedecodevalue.id ?? 0, codedecodevalue.code ?? '')}
                >
                  {deletingId === codedecodevalue.id ? 'Deleting...' : 'Delete'}
                </Menu.Item>
              </Menu>
            }
          >
            <MoreOutlined />
          </Dropdown>
        ),
      },
    ],
  };

  const getCurrentState = (): CodeDecodeValueListRequest => {
    const urlParams = new URLSearchParams(location.search);
    return {
      codeDecodeTypeId: Number(params.codedecodetypeid),
      current: Number(urlParams.get('p')) || DefaultPage,
      pageSize: Number(urlParams.get('size')) || DefaultPageSize,
      filter: urlParams.get('q'),
      sortBy: urlParams.get('sortBy') || 'EnteredByDate Desc',
    };
  };

  const [pagination, setPagination] = useState<CodeDecodeValueListRequest>(getCurrentState());

  const {
    data: pagedData = {},
    isFetching,
    error,
  }: {
    data?: PaginatedResult<CodeDecodeValueViewModel> | undefined;
    isFetching?: boolean | undefined;
    error?: any;
  } = useFetchCodeDecodeValuesQuery(pagination, { pollingInterval: DefaultPollingInterval });

  useEffect(() => {
    const newState = getCurrentState();
    setPagination(newState);
  }, [location.search]);

  const {
    deletingId,
    isDeleting: isDeleteLoading,
    deleteError,
    deleteErrorMessage,
    deleteEntity,
  } = useMasterDataDelete({
    entityType: 'CodeDecodeValues',
    tagType: 'CodeDecodeValue',
    entityLabel: 'code decode value',
  });
  const { confirm } = Modal;

  const onCreateNew = useCallback(
    () => navigate(getLink('CodeDecodeValueEdit', { ...params, Id: 'new' })),
    [params, navigate]
  );

  const onBackToCodeDecodeType = () => {
    navigate(
      getLink('CodeDecodeTypeList', {
        ...params,
        tenantId: params.tenantId,
      })
    );
  };
  const handleTableChange = (
    paginationConfig: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<CodeDecodeValueViewModel> | SorterResult<CodeDecodeValueViewModel>[],
    extra: TableCurrentDataSource<CodeDecodeValueViewModel>
  ) => {
    // Handle sorter - it can be null, undefined, a single object, or an array
    const sorterResult = Array.isArray(sorter) ? sorter[0] : sorter;
    const { columnKey, order } = (sorterResult || {}) as SorterResult<CodeDecodeValueViewModel>;

    const defaultSort = 'EnteredByDate Desc';
    // Read current sort from URL to ensure fresh value
    const urlParams = new URLSearchParams(location.search);
    const currentSortBy = urlParams.get('sortBy') || defaultSort;
    const currentPage = Number(urlParams.get('p')) || 1;
    
    // Determine new page number - ensure it's always a valid number
    let newPage = paginationConfig.current;
    if (!newPage || newPage < 1) {
      newPage = currentPage;
    }
    
    // Determine new sort - always ensure it's a valid string
    let newSortBy: string = defaultSort;

    // Check if we're actually changing sort or just pagination
    const isSortingAction = columnKey && order;
    const isCancellingSort = columnKey && !order && currentSortBy !== defaultSort;
    
    if (isSortingAction) {
      const columnMapping: { [key: string]: string } = {
        'Id': 'Id',
        'code': 'Code',
      };
      
      const clickedColumn = columnKey.toString();
      const backendColumnName = columnMapping[clickedColumn] || clickedColumn;
      newSortBy = getSortBy(backendColumnName, order);
      
      if (newSortBy !== currentSortBy) {
        newPage = 1;
      }
    } else if (isCancellingSort) {
      newSortBy = defaultSort;
      newPage = 1;
    } else {
      // Pagination only - preserve current sort
      newSortBy = currentSortBy || defaultSort;
    }
    
    // Final safety check - ensure newSortBy is never empty
    if (!newSortBy || newSortBy.trim() === '') {
      newSortBy = defaultSort;
    }

    const newUrl = getLink(
      'CodeDecodeValueList',
      { ...params },
      {
        sortBy: newSortBy,
        p: newPage,
        size: paginationConfig.pageSize,
        q: urlParams.get('q') || '',
      }
    );
    
    navigate(newUrl, { replace: false });
  };

  const handleSearch = (searchText: string) => {
    navigate(
      getLink(
        'CodeDecodeValueList',
        { ...params },
        {
          p: DefaultPage,
          size: pagination.pageSize,
          q: searchText?.length ? `Code:*${searchText.replaceAll(" ", "\\ ")}*` : '',
          sortBy: pagination.sortBy || '',  // Preserve current sort
        }
      )
    );
  };


  return (
    <div>
      <PageHeader
        extra={
            <Space>
                  <Button htmlType="button" data-testid={'create-new-codedecodevalue'} onClick={onCreateNew}>
                  New Code Decode Value
                  </Button>
                </Space>
        }
        title={
          <Title
            level={4}
            style={{
              marginBottom: 0,
              display: 'inline-block',
            }}
          >
            Code Decode Values
          </Title>
        }
        breadcrumb={
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to={getLink('home', { tenantId: params.tenantId })}>
              <HomeOutlined />
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>              
              <Link to={getLink('cob', { tenantId: params.tenantId })}>
              Dashboard
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to={getLink('CodeDecodeTypeList', { ...props, tenantId:params.tenantId })}>Code Decode Types</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item key="codedecodevalues">
              <Link to={getLink('CodeDecodeValueList', { tenantId: params.tenantId,codedecodetypeid: params.codedecodetypeid })}>CodeDecodeValues</Link>
            </Breadcrumb.Item>
          </Breadcrumb>
        }
      >
        <Card>
          {error?.status && (
            <Alert message="Error" description={error.data} type="error" showIcon />
          )}
          {deleteError && (
            <Alert message="Error" description={deleteErrorMessage} type="error" showIcon />
          )}
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Input.Search
              placeholder="Search by Code"
              onSearch={handleSearch}
              enterButton
              style={{ width: 400 }}
              loading={isFetching}
              allowClear
            />
            <Table
            scroll={{ x: true }}
              dataSource={pagedData.results}
              onChange={handleTableChange}
              rowKey={'id'}
              pagination={{
                ...pagination,
                total: pagedData.totalCount,
                showTotal: (total) => `Total ${total} items`,
                pageSizeOptions: ["10", "20", "50","100"],
                showSizeChanger: true,
                locale: { items_per_page: " / Page" }
              }}
              columns={codedecodevalueTable.columns}
              loading={isFetching}
              onRow={(record, rowIndex) => {
                return {
                  onClick: event => { 
                    setEntityId(record?.id||0);
                  },
                };
              }}
            />
          </Space>
        </Card>
        {showAuditLogs?<AuditLogsScreen entityId={entityId} entityName={'CodeDecodeValues'} showAuditLogs={setShowAuditLogs}/>:<></>}
      </PageHeader>
    </div>
  );
}
